import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import * as Sentry from '@sentry/react';

import { HorizontalMenu } from '../../components/horizontal-menu/HorizontalMenu';
import { DirectorPage, DirectorPageProps } from './pages/director.page';
import { LineProducerPage, LineProducerPageProps } from './pages/line-producer.page';
import { CommonFilePage, CommonFilePageProps } from './pages/common-file.page';
import { LocationPage, LocationPageProps } from './pages/location.page';
import { CastingPage, CastSelectionPageProps } from './pages/casting.page';
import { CrewPageProps, TeamPage } from './pages/team.page';
import { LoaderContainer } from './components/step-container';
import { PageLoader } from '../../app/components/page-loader';
import { CrashErrorScreen } from './components/crash-error-screen';
import { useCurrentProject } from '../../hooks/use-current-project';
import { useCurrentStep } from '../../hooks/use-current-step';
import { ShootingDayScreen } from '../../features/shootingdays/screens/shooting-day.screen';
import { ScheduleScreen } from '../../features/schedule/screens/schedule.screen';
import { ProducerScreen } from '../../features/collaborators/screens/producer.screen';
import { uiStore } from '../../core/stores/ui-store';
import { StoryboardScreen } from '../../features/storyboards/screens/storyboard.screen';
import { TaskPanel } from '../../features/tasks/components/task.panel';
import { context } from '../../app/context';

import styles from './project-step.route.module.scss';
type ScreenType = Record<
  string,
  | React.FC<CommonFilePageProps>
  | React.FC<DirectorPageProps>
  | React.FC<LineProducerPageProps>
  | React.FC<LocationPageProps>
  | React.FC<CastSelectionPageProps>
  | React.FC<CrewPageProps>
  | React.FC<any>
>;

const screenType: ScreenType = {
  default: CommonFilePage,
  schedule: ScheduleScreen,
  storyboard: StoryboardScreen,
  producer: ProducerScreen,
  director: DirectorPage,
  lineProducer: LineProducerPage,
  video: CommonFilePage,
  review: CommonFilePage,
  wip: CommonFilePage,
  location: LocationPage,
  casting: CastingPage,
  shootingDay: ShootingDayScreen,
  shotList: ScheduleScreen,
  crew: TeamPage,
};

export const ProjectStepRoute = observer(() => {
  const project = useCurrentProject();
  const step = useCurrentStep();

  useEffect(() => {
    return () => uiStore.closeTaskPanel();
  }, [step]);

  useEffect(() => {
    context.step = step;

    return () => {
      context.step = undefined;
    };
  }, [step]);

  let Screen = step && screenType[step.type];

  if (!Screen) {
    Screen = screenType['default'];
  }

  if (!step) {
    return (
      <LoaderContainer>
        <PageLoader />
      </LoaderContainer>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<CrashErrorScreen />}>
      <HorizontalMenu />

      <div className={styles['content']}>
        <Screen project={project} step={step} />

        {uiStore.isTaskPanelOpen && !!uiStore.taskPanelTargetId && (
          <TaskPanel targetId={uiStore.taskPanelTargetId} insideStep />
        )}
      </div>
    </Sentry.ErrorBoundary>
  );
});
